import { getWindowDimensions, mapWidthToThresholds } from '@util/windowDimensions';
import { loadGapi } from './loadGapi';
import { loadGapiLibrary } from './loadGapiLibrary';

type PickerFile = gapi.client.drive.File;
type PickerOpener = () => Promise<PickerFile[]>;

export const loadGooglePicker = async (
  clientId: string,
  devKey: string,
  appId: string
): Promise<PickerOpener> => {
  await loadGapi();

  const authPromise = loadGapiLibrary('auth2');
  const pickerPromise = loadGapiLibrary('picker');
  const drivePromise = loadGapiLibrary('client').then(() => window.gapi.client.load('drive', 'v3'));

  await Promise.all([authPromise, pickerPromise, drivePromise]);

  const authorize = (): Promise<gapi.auth2.AuthorizeResponse> =>
    new Promise((resolve, reject) => {
      window.gapi.auth2.authorize(
        {
          client_id: clientId,
          scope: 'https://www.googleapis.com/auth/drive.file',
          prompt: 'select_account',
        },
        (res) => {
          if (res.error) {
            reject(res.error);
          }
          resolve(res);
        }
      );
    });

  const awaitPickerSelection = (picker: google.picker.Picker) =>
    new Promise<google.picker.ResponseObject>((resolve, reject) => {
      picker.setCallback((response) => {
        if (response[google.picker.Response.ACTION] === google.picker.Action.PICKED) {
          resolve(response);
        } else if (response[google.picker.Response.ACTION] === google.picker.Action.CANCEL) {
          reject();
        }
      });
    });

  const getPicker = (auth: gapi.auth2.AuthorizeResponse) => {
    const pickerBuilder = new google.picker.PickerBuilder()
      .addView(google.picker.ViewId.DOCUMENTS)
      .setMaxItems(1)
      .setOAuthToken(auth.access_token)
      .setDeveloperKey(devKey)
      .setAppId(appId);

    const { isAtLeastSmall } = mapWidthToThresholds(getWindowDimensions().width);

    if (!isAtLeastSmall) {
      pickerBuilder.setSize(566, 500);
    }

    return pickerBuilder.build();
  };

  const getSelectedFiles = async (): Promise<PickerFile[]> => {
    const auth = await authorize();

    const picker = getPicker(auth);
    picker.setVisible(true);
    const selection = await awaitPickerSelection(picker);

    window.gapi.client.setToken(auth);

    const promises = selection.docs.map(async (doc) => {
      await window.gapi.client.drive.permissions.create({
        fileId: doc.id,
        resource: {
          type: 'anyone',
          role: 'reader',
        },
      });

      const res = await window.gapi.client.drive.files.get({
        fileId: doc.id,
        fields: 'webViewLink,name',
      });

      if (!res.result.name) {
        throw new Error('No file name found');
      }

      if (!res.result.webViewLink) {
        throw new Error('No sharing link found');
      }

      return res.result;
    });

    const files = await Promise.all(promises);

    return files;
  };

  return getSelectedFiles;
};
